/**
 * How a bill is being treated when cash is short.
 *
 * The order matters: it is the order things get paid in, and every list in the
 * app sorts by it. A label on its own is easy to apply carelessly, so a reason
 * is required alongside — the pair is what can be handed to a finance director
 * or an investor and stand up to a question.
 *
 * Deliberately few. A scale with ten points invites agonising over which of two
 * adjacent ones applies; five forces the decision that actually matters, which
 * is whether this is paid now or argued about later.
 */
export const PRIORITIES = [
  {
    key: "must_pay",
    label: "Must pay",
    blurb: "Work stops, or people do not get paid, without it",
    tone: "#8C2A2A",
    pay: true,
  },
  {
    key: "risk",
    label: "Risk if unpaid",
    blurb: "Legal action, a service cut off, or credit damaged",
    tone: "#A6531F",
    pay: true,
  },
  {
    key: "small_supplier",
    label: "Small supplier",
    blurb: "Cannot absorb the delay — paid on principle, not pressure",
    tone: "#2F5D41",
    pay: true,
  },
  {
    key: "can_push",
    label: "Worth pushing",
    blurb: "The relationship can take a conversation about timing",
    tone: "#5B5BA6",
    pay: false,
  },
  {
    key: "no_rush",
    label: "No rush",
    blurb: "Nothing happens if this waits",
    tone: "#6B6B60",
    pay: false,
  },
];

export const PRIORITY = Object.fromEntries(PRIORITIES.map((p) => [p.key, p]));

/** Where an undecided bill sorts: after everything decided, not silently last. */
export const priorityRank = (key) => {
  const i = PRIORITIES.findIndex((p) => p.key === key);
  return i === -1 ? PRIORITIES.length : i;
};

/** Accepts a label, a key, or something close, so an import is forgiving. */
export function parsePriority(value) {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return null;
  const direct = PRIORITIES.find((p) => p.key === v || p.label.toLowerCase() === v);
  if (direct) return direct.key;
  // The words people actually type into a spreadsheet.
  if (/critical|essential|urgent|must/.test(v)) return "must_pay";
  if (/risk|legal|cut ?off|threat/.test(v)) return "risk";
  if (/small|sole|freelanc|individual/.test(v)) return "small_supplier";
  if (/push|defer|delay|later|negotiat/.test(v)) return "can_push";
  if (/no rush|hold|wait|low/.test(v)) return "no_rush";
  return null;
}
